import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileDateTimePicker } from "@mui/x-date-pickers/MobileDateTimePicker";
import PopoverAG from "components/ui/popoverAG";
import { format } from "date-fns";
import useApi from "hooks/useApi";
import usePermissions from "hooks/usePermissions";
import { useEffect, useState } from "react";
import { BsChevronExpand } from "react-icons/bs";
import { MdCancel, MdCheck, MdEdit } from "react-icons/md";
import { useSelector } from "react-redux";

const StatusHistory = ({ row_id }) => {
  const permissions = usePermissions("project-customer");

  const [statusHistory, setStatusHistory] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [editNote, setEditNote] = useState("");
  const [editingDueDate, setEditingDueDate] = useState(null);
  const [newDueDate, setNewDueDate] = useState(new Date());
  const [reload, setReload] = useState(false);

  const isAdmin = useSelector(
    (state) => state.auth.user_details.role === "Admin"
  );

  let { fetchData, loading } = useApi();
  // //  status history api call
  useEffect(() => {
    async function callApi() {
      let endpoint = {
        method: "get",
        url: "/api/admin/project-customer/status-track/history/" + row_id,
      };
      let result = await fetchData(endpoint, false);
      if (result?.success) {
        setStatusHistory(result?.data);
      }
    }
    callApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  //  status history note
  const handleNoteClick = (id) => {
    setExpandedRow(expandedRow === id ? null : id);
    setEditingRow(null);
  };

  //  status history note edit
  const handleEditClick = (id, note) => {
    setEditingRow(id);
    setEditNote(note || "");
  };

  //  status history note update
  const handleSubmitNote = async (id) => {
    let endpoint = {
      method: "post",
      url: `/api/admin/project-customer/status-track/note/update/${editingRow}`,
      data: { note: editNote },
    };
    let result = await fetchData(endpoint, false);
    if (result?.success) {
      setStatusHistory(
        statusHistory.map((item) =>
          item.id === id ? { ...item, note: editNote } : item
        )
      );
      setEditingRow(null);
    }
  };

  // handle due date edit
  const handleDueDateEdit = (id, dueDate) => {
    setEditingDueDate(id);
    setNewDueDate(new Date(dueDate));
  };

  // handle due date update
  const handleDueDateUpdate = async (id) => {
    let endpoint = {
      method: "post",
      url: `/api/admin/project-customer/status-track/due-status/update/${id}`,
      data: { due_date: format(newDueDate, "yyyy-MM-dd'T'HH:mm:ss") },
    };
    let result = await fetchData(endpoint, false);
    if (result?.success) {
      setReload((prev) => !prev);
      setEditingDueDate(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Skeleton variant="text" width={200} height={32} sx={{ mb: 2 }} />
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {[...Array(6)].map((item) => (
                  <TableCell key={item}>
                    <Skeleton variant="text" width={100} />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {[...Array(6)].map((row) => (
                <TableRow key={row}>
                  {[...Array(6)].map((cell) => (
                    <TableCell key={cell}>
                      <Skeleton variant="text" width={80} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Status History
      </Typography>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Status Name</TableCell>
              <TableCell>Updated By</TableCell>
              <TableCell>Status Updated Date</TableCell>
              <TableCell>Due Date & Time</TableCell>
              <TableCell>Timespan</TableCell>
              <TableCell>Note</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {statusHistory.map((item) => (
              <>
                <TableRow
                  key={item.id}
                  sx={{
                    bgcolor:
                      item?.timespan?.split(" ")[0] === "Delayed"
                        ? "#ffebee"
                        : "white",
                    "& td": {
                      borderBottom:
                        expandedRow === item.id ? "none" : undefined,
                    },
                    cursor: "pointer",
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      {item?.project_status_customer?.name}
                      {item.status === 1 && (
                        <Chip
                          label="Active"
                          sx={{
                            bgcolor: "#4caf50",
                            color: "#fff",
                            fontSize: "12px",
                          }}
                          size="small"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        display: "flex",
                      }}
                    >
                      <PopoverAG items={[item?.updated_by]} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {format(new Date(item?.created_at), "MMM dd, yyyy")}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {format(new Date(item?.created_at), "hh:mm a")}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {editingDueDate === item.id ? (
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "center" }}
                      >
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <MobileDateTimePicker
                            label="Due Date"
                            value={newDueDate}
                            onChange={(newValue) => setNewDueDate(newValue)}
                            inputFormat="yyyy/MM/dd hh:mm a"
                            renderInput={(params) => (
                              <TextField size="small" {...params} />
                            )}
                          />
                        </LocalizationProvider>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            gap: 1,
                            justifyContent: "flex-end",
                          }}
                        >
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleDueDateUpdate(item.id)}
                            sx={{
                              backgroundColor: (theme) =>
                                theme.palette.primary.main,
                              color: "#fff",
                              borderRadius: "8px",
                              padding: "8px",
                              boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                backgroundColor: (theme) =>
                                  theme.palette.primary.dark,
                                boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
                              },
                            }}
                          >
                            <MdCheck />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => setEditingDueDate(null)}
                            sx={{
                              borderRadius: "8px",
                              padding: "8px",
                              border: "1px solid rgba(0, 0, 0, 0.12)",
                              transition: "all 0.2s ease",
                              "&:hover": {
                                backgroundColor: "rgba(211, 47, 47, 0.04)",
                                borderColor: "error.main",
                                color: "error.main",
                              },
                            }}
                          >
                            <MdCancel />
                          </IconButton>
                        </Box>
                      </Box>
                    ) : (
                      <Box>
                        {item?.due_date ? (
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500 }}
                              >
                                {format(
                                  new Date(item?.due_date),
                                  "MMM dd, yyyy"
                                )}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="textSecondary"
                              >
                                {format(new Date(item?.due_date), "hh:mm a")}
                              </Typography>
                            </Box>
                            {!!permissions?.["status due date update"] && (
                              <IconButton
                                onClick={() =>
                                  handleDueDateEdit(item.id, item.due_date)
                                }
                                size="small"
                                sx={{
                                  ml: 1,
                                  color: "primary.main",
                                  "&:hover": {
                                    backgroundColor: "rgba(25, 118, 210, 0.04)",
                                  },
                                }}
                              >
                                <MdEdit />
                              </IconButton>
                            )}
                          </Box>
                        ) : (
                          "-"
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>{item?.timespan}</TableCell>
                  <TableCell>
                    <Box
                      sx={{
                        position: "relative",
                        cursor: "pointer",
                        "&:hover": {
                          "& svg": {
                            color: (theme) => theme.palette.primary.main,
                          },
                        },
                        "& svg": {
                          position: "absolute",
                          color:
                            expandedRow === item.id
                              ? (theme) => theme.palette.primary.main
                              : "#777",
                          top: "0px",
                          right: "0px",
                          cursor: "pointer",
                          fontSize: "17px",
                        },
                      }}
                      onClick={() => handleNoteClick(item.id)}
                    >
                      <Typography
                        sx={{
                          overflow: "hidden",
                          WebkitLineClamp: "1",
                          WebkitBoxOrient: "vertical",
                          textOverflow: "ellipsis",
                          maxWidth: "150px",
                          maxHeight: "22px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {item?.note || "-"}
                      </Typography>
                      <BsChevronExpand />
                    </Box>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    sx={{
                      paddingBottom: 0,
                      paddingTop: 0,
                    }}
                    colSpan={6}
                  >
                    <Collapse
                      in={expandedRow === item.id}
                      timeout="auto"
                      unmountOnExit
                    >
                      <Box
                        sx={{
                          minHeight: "200px",
                          maxWidth: "580px",
                          border: "1px solid #ccc",
                          borderRadius: "5px",
                          overflow: "hidden",
                          padding: "10px",
                          margin: "10px auto",
                          zIndex: "-1",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            borderBottom: "1px solid #ccc",
                            paddingBottom: "10px",
                            marginBottom: "10px",
                          }}
                        >
                          <Typography fontWeight={500}>
                            Note
                            {item?.note_updated_by?.name && (
                              <Typography
                                fontSize={12}
                                component={"span"}
                                ml={1}
                              >
                                Updated By: {item?.note_updated_by?.name}
                              </Typography>
                            )}
                          </Typography>
                          {!!isAdmin && (
                            <IconButton
                              variant="outlined"
                              onClick={() =>
                                handleEditClick(item.id, item.note)
                              }
                            >
                              <MdEdit />
                            </IconButton>
                          )}
                        </Box>

                        {editingRow === item.id ? (
                          <Box
                            sx={{
                              display: "flex",
                              gap: 2,
                              alignItems: "flex-start",
                            }}
                          >
                            <TextField
                              multiline
                              rows={4}
                              fullWidth
                              value={editNote}
                              onChange={(e) => setEditNote(e.target.value)}
                              variant="outlined"
                            />
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "10px",
                              }}
                            >
                              <Button
                                variant="solid"
                                onClick={() => handleSubmitNote(item.id)}
                              >
                                Submit
                              </Button>
                              <Button
                                variant="outlined"
                                onClick={() => setEditingRow(null)}
                              >
                                Cancel
                              </Button>
                            </Box>
                          </Box>
                        ) : (
                          <>
                            <Typography>{item?.note}</Typography>
                          </>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default StatusHistory;
